import { useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const VOICE_GROUPS = [
  { value: '', label: 'Geen stemgroep' },
  { value: 'SOPRAAN', label: 'Sopraan' },
  { value: 'ALT', label: 'Alt' },
  { value: 'TENOR', label: 'Tenor' },
  { value: 'BAS', label: 'Bas' },
];

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const { user } = useAuth();
  const [voiceGroup, setVoiceGroup] = useState(user?.voiceGroup || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(user?.photo || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsLoading(true);
    setError('');

    try {
      await api.updateProfile({
        voiceGroup: voiceGroup || null,
      });
      onClose();
      window.location.reload(); // Refresh to update user data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Alleen afbeeldingen zijn toegestaan');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Afbeelding mag maximaal 5MB zijn');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const result = await api.uploadProfilePhoto(file);
      setPhotoPreview(result.photo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Profiel bewerken</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {/* Photo */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-purple-100 flex items-center justify-center mb-3">
            {photoPreview ? (
              <img
                src={photoPreview}
                alt={user?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-3xl text-purple-600 font-semibold">
                {user?.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="text-sm text-purple-600 hover:text-purple-700 disabled:opacity-50"
          >
            {isUploading ? 'Uploaden...' : 'Foto wijzigen'}
          </button>
        </div>

        {/* Name (read-only) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Naam
          </label>
          <input
            type="text"
            value={user?.name || ''}
            disabled
            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            Neem contact op met de beheerder om je naam te wijzigen
          </p>
        </div>

        {/* Voice Group */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stemgroep
          </label>
          <select
            value={voiceGroup}
            onChange={(e) => setVoiceGroup(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
          >
            {VOICE_GROUPS.map(group => (
              <option key={group.value} value={group.value}>
                {group.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
          >
            {isLoading ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
